import { createCollection } from '@rocketlane/state-db';
import { ProjectId } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { queryClient } from 'queries/client';
import { getProjectSprintsQueryKey } from 'queries/common';
import { logger } from 'services';
import { isTaskPaneMiniEnabled } from './config';
import { mapTaskChangesToPayload, toCreatePayload } from './payload';
/**
 * The single source of truth for Task entities. Replaces the tasks Redux
 * slice, the React Query task caches, AgGrid row data, and the three parallel
 * optimistic systems (Redux / RQ / AgGrid nodes).
 *
 * Data enters via `push()` from: eager list loads, paginated AgGrid fetches,
 * detail fetches, and WS refetches. Mutations apply optimistically and route
 * to the correct endpoint based on which `Task` field changed.
 *
 * Callers express changes as real `Task` fields — e.g. a phase change is
 * `update(id, { projectPhase: { projectPhaseId } })`, not a flat patch. The
 * handler below translates those into the (sometimes differently-shaped) API
 * payloads.
 */ /**
 * Dev-only diagnostic for the "lite list stomps hydrated detail" bug class.
 *
 * List endpoints (`/home`, kanban, paginated grids, …) return trimmed task
 * DTOs; single-task detail, WS refetches and mutation responses return the
 * full record. The backend serializes `NON_NULL`, so a trimmed payload simply
 * OMITS the detail-only keys. A REPLACE push of such a payload over a hydrated
 * record silently drops them — and if the task drawer is open on that task,
 * the description / attachments / dependencies blank out. Lite sources must go
 * through `mergeTasksIntoCollection` instead.
 *
 * `isNewer` sees the value about to be stored next to the current one. In
 * merge mode the incoming value is already `{ ...existing, ...incoming }`, so
 * nothing can be missing and this never fires; only a replace push can drop
 * keys. Two or more of the markers below vanishing at once is the signature
 * of a trimmed DTO rather than a user clearing a single value (which the
 * `NON_NULL` serializer also renders as an omitted key). Warns once per task
 * per page load; never affects what is stored.
 */ export const DETAIL_ONLY_TASK_KEYS = [
    'taskDescription',
    'attachments',
    'dependentTasks',
    'followers'
];
const DOWNGRADE_MIN_DROPPED_KEYS = 2;
const warnedDowngradedTaskIds = new Set();
const isDowngradeDiagnosticEnabled = ()=>typeof process !== 'undefined' && process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test';
/** Detail-only keys present on `existing` that `incoming` would drop. */ export const droppedDetailKeys = (incoming, existing)=>DETAIL_ONLY_TASK_KEYS.filter((key)=>existing[key] !== undefined && incoming[key] === undefined);
export const warnOnDetailDowngrade = (incoming, existing)=>{
    if (!isDowngradeDiagnosticEnabled() || warnedDowngradedTaskIds.has(incoming.taskId)) {
        return;
    }
    const dropped = droppedDetailKeys(incoming, existing);
    if (dropped.length < DOWNGRADE_MIN_DROPPED_KEYS) {
        return;
    }
    warnedDowngradedTaskIds.add(incoming.taskId);
    logger.warn('taskCollection: replace push dropped detail-only keys — lite list payload over a hydrated task? Push list DTOs via mergeTasksIntoCollection.', {
        taskId: incoming.taskId,
        dropped
    });
};
/** Test seam: forget which tasks already warned. */ export const resetDetailDowngradeWarnings = ()=>{
    warnedDowngradedTaskIds.clear();
};
const getProjectId = (task)=>{
    var _task_project;
    return task === null || task === void 0 ? void 0 : (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
};
/**
 * Merge a partial `fields[]` patch onto the existing fields BY `fieldId`.
 *
 * A custom-field edit sends only the changed field (`{ fields: [oneField] }`).
 * A shallow `{ ...task, ...changes }` would REPLACE the whole `fields[]` with
 * that single-element array, dropping every other custom field from the
 * optimistic value (and, on an empty-body commit, from the committed value).
 * Merge by id instead: replace matching fields, append new ones, keep the rest.
 */ export const mergeTaskFields = (existing = [], incoming)=>{
    const next = existing.map((f)=>({
            ...f
        }));
    incoming.forEach((patch)=>{
        if ((patch === null || patch === void 0 ? void 0 : patch.fieldId) == null) {
            return;
        }
        const idx = next.findIndex((f)=>f.fieldId === patch.fieldId);
        if (idx >= 0) {
            next[idx] = {
                ...next[idx],
                ...patch
            };
        } else {
            next.push(patch);
        }
    });
    return next;
};
const routeUpdate = async ({ id, changes, original, metadata })=>{
    var _patch_projectPhase;
    const taskId = id;
    const patch = changes !== null && changes !== void 0 ? changes : {};
    const projectId = getProjectId(original);
    // Kanban drop → the board's own endpoint. Matched FIRST: a drop onto a phase
    // column produces a `projectPhase` patch, which the branch below would
    // otherwise send to `updatePhase` — losing the drop validation and the cell
    // data the board needs back.
    const kanbanDrop = metadata === null || metadata === void 0 ? void 0 : metadata.kanbanDrop;
    if (kanbanDrop) {
        const { isAllowed, task } = await kanbanDrop.persist();
        if (!isAllowed) {
            // Throwing is what rolls the overlay back — and, now that overlays are
            // broadcast, what retracts it on every peer too.
            throw new Error(`kanban drop rejected for task ${taskId}`);
        }
        return task;
    }
    // Phase change → dedicated endpoint (returns the updated Task).
    if (((_patch_projectPhase = patch.projectPhase) === null || _patch_projectPhase === void 0 ? void 0 : _patch_projectPhase.projectPhaseId) != null) {
        var _patch_startDate, _patch_dueDate;
        return TaskAPI.updatePhase(taskId, {
            projectPhase: {
                projectPhaseId: patch.projectPhase.projectPhaseId
            },
            startDate: (_patch_startDate = patch.startDate) !== null && _patch_startDate !== void 0 ? _patch_startDate : original === null || original === void 0 ? void 0 : original.startDate,
            dueDate: (_patch_dueDate = patch.dueDate) !== null && _patch_dueDate !== void 0 ? _patch_dueDate : original === null || original === void 0 ? void 0 : original.dueDate
        });
    }
    // Privacy toggle → dedicated endpoint.
    if (patch.private !== undefined) {
        return TaskAPI.updatePrivacy(taskId, {
            private: patch.private
        });
    }
    // Subtask reorder → dedicated endpoint (returns void → optimistic committed).
    if (patch.subTaskOrder !== undefined) {
        await TaskAPI.updateSubTaskOrder(taskId, {
            subTaskOrder: patch.subTaskOrder
        });
        return undefined;
    }
    // Default field update — mini vs full per the pane flag (legacy parity).
    const body = mapTaskChangesToPayload(patch);
    if (projectId == null) {
        throw new Error(`taskCollection.update: missing projectId for task ${taskId}`);
    }
    if (isTaskPaneMiniEnabled()) {
        // The mini endpoint's response is unreliable as a source of truth: it can
        // drop a just-added assignee and return a STALE `taskEffortSetting` (e.g.
        // `EQUAL_SPLIT` immediately after the user switched to custom). Committing
        // it would clobber the user's optimistic change and empty the effort table.
        // The legacy update-task saga deliberately ignored the mini response and
        // kept the existing task — match it exactly: fire the call, then return
        // undefined so the collection commits the optimistic merged value (which
        // carries the user's intent). Effort rows are derived client-side from
        // assignee + effort + setting, so no server-computed effortMeta is needed
        // to render them.
        await TaskAPI.updateTaskMini(projectId, taskId, body);
        return undefined;
    }
    return TaskAPI.updateTask(projectId, taskId, body);
};
// Sprints are not collection-backed: a task edit can shift its sprint's progress
// counts, so that React Query cache needs invalidating separately. Scoped to the
// fields that can actually move a counter — every task edit would otherwise cost
// an extra /sprints GET.
const SPRINT_PROGRESS_FIELDS = [
    'fields',
    'projectPhase'
];
const onUpdate = async (context)=>{
    const result = await routeUpdate(context);
    const projectId = getProjectId(context.original);
    const touchesSprintProgress = context.changes === undefined || SPRINT_PROGRESS_FIELDS.some((field)=>field in context.changes);
    if (projectId != null && touchesSprintProgress) {
        queryClient.invalidateQueries(getProjectSprintsQueryKey(ProjectId(projectId)));
    }
    return result;
};
export const taskCollection = createCollection({
    id: 'tasks',
    getKey: (task)=>task.taskId,
    // Secondary index by project so `getProjectTasksFromCollection` (and any
    // per-project live query) is O(result) instead of an O(n) `getAll().filter`
    // scan over every task in memory. `projectId` is stable (a task does not
    // change project optimistically), which is the requirement for indexing.
    indexes: {
        byProject: (task)=>{
            var _task_project;
            return (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
        },
        // Per-phase index so each swimlane subscribes to ONLY its phase's tasks: an
        // edit in phase A leaves `getByIndex('byPhase', B)` reference-equal, so
        // phase B's swimlane does not re-render. (A task's phase is stable except
        // via an explicit move, which reindexes on commit.)
        byPhase: (task)=>{
            var _task_projectPhase;
            return (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId;
        },
        // Per-parent index so a subtask list listens to ONLY its own subtasks (a
        // freshly-created subtask is pushed with `parentTask` set and appears
        // immediately, without the parent's `subTaskOrder` having to refresh).
        byParent: (task)=>task.parentTask
    },
    // Server payloads pushed in are authoritative; the read model has no
    // reliable monotonic version field, so always replace. The hook doubles as
    // the dev-only downgrade diagnostic (see `warnOnDetailDowngrade`).
    isNewer: (incoming, existing)=>{
        warnOnDetailDowngrade(incoming, existing);
        return true;
    },
    // Custom-field edits patch a single field; merge it into the existing
    // `fields[]` by id so the optimistic overlay keeps the other fields.
    mergeChanges: (original, changes)=>{
        const merged = {
            ...original,
            ...changes
        };
        if (changes.fields) {
            merged.fields = mergeTaskFields(original.fields, changes.fields);
        }
        return merged;
    },
    onInsert: async ({ modified })=>{
        const projectId = getProjectId(modified);
        if (projectId == null) {
            throw new Error('taskCollection.insert: missing project on new task');
        }
        // Server returns the real task; the collection swaps the temp negative id.
        return TaskAPI.createTask(projectId, toCreatePayload(modified !== null && modified !== void 0 ? modified : {}));
    },
    onUpdate,
    onDelete: async ({ id, original, metadata })=>{
        var _ref;
        // Prefer an explicitly-supplied project id over the stored task's. Callers
        // that already know it (`deleteTaskRecord` takes it as an argument) must not
        // be at the mercy of how fully the local copy happens to be hydrated: list
        // and board payloads can carry a store-shaped or absent `project`, and
        // re-deriving it there turned a valid delete into "missing projectId".
        const meta = metadata;
        const projectId = (_ref = meta === null || meta === void 0 ? void 0 : meta.projectId) !== null && _ref !== void 0 ? _ref : getProjectId(original);
        if (projectId == null) {
            throw new Error(`taskCollection.delete: missing projectId for task ${String(id)}`);
        }
        await TaskAPI.deleteTask(projectId, id, meta === null || meta === void 0 ? void 0 : meta.actor);
    }
});
/**
 * Imperative (non-hook) read of a project's tasks from the collection.
 * Replaces `queryClient.getQueryData(getProjectTasksQueryKey(projectId))` —
 * the task-list RQ cache no longer exists. Returns api-shaped tasks.
 *
 * O(result) via the `byProject` index — not an O(n) scan over every task. This
 * matters on the edit hot path: callers re-run this on collection changes, so
 * an O(n) filter here turned each optimistic edit into a full-collection sweep.
 */ export const getProjectTasksFromCollection = (projectId)=>taskCollection.getByIndex('byProject', projectId);
/**
 * Merge tasks into the collection WITHOUT downgrading existing entries.
 *
 * Some sources (e.g. the kanban board, the paginated plan board's entity
 * pages) fetch task payloads that carry fewer fields than a full detail fetch
 * — and, with `excludeMetaDataInFields`, `fields[]` entries stripped of their
 * option metadata. A plain `push` would replace the fuller entry wholesale;
 * this overlays the incoming top-level fields onto whatever is already stored
 * AND merges `fields[]` element-wise by `fieldId` (same rule as
 * `mergeChanges`), so detail-only fields and field metadata survive. New
 * tasks are inserted as-is.
 */ export const mergeTasksIntoCollection = (tasks, opts)=>{
    if (tasks.length === 0) {
        return;
    }
    taskCollection.push(buildMergedTasks(tasks), opts);
};
/**
 * The no-downgrade merge of `mergeTasksIntoCollection`, without the push. Split
 * out so callers that must push through a DIFFERENT channel — cross-tab sync,
 * whose push carries a `source` tag that stops it echoing back — reuse the exact
 * same merge rules instead of re-deriving them.
 */ export const buildMergedTasks = (tasks)=>tasks.map((task)=>{
        var _task_parentTaskObject;
        var _task_parentTaskObject1, _existing_parentTaskObject;
        // Merge onto the SYNCED value — pushing the optimistic overlay into
        // the backing store would survive a rollback as fake server truth.
        const existing = taskCollection.getSynced(task.taskId);
        if (!existing) {
            return task;
        }
        return {
            ...existing,
            ...task,
            // No-downgrade: keep the api-shaped `project` object the `byProject`
            // index reads; a store-shaped `project` (a number) would drop the task
            // out of the index.
            project: task.project && typeof task.project === 'object' ? task.project : existing.project,
            parentTaskObject: ((_task_parentTaskObject1 = task.parentTaskObject) === null || _task_parentTaskObject1 === void 0 ? void 0 : _task_parentTaskObject1.taskId) != null && task.parentTaskObject.taskId === ((_existing_parentTaskObject = existing.parentTaskObject) === null || _existing_parentTaskObject === void 0 ? void 0 : _existing_parentTaskObject.taskId) ? {
                ...existing.parentTaskObject,
                ...task.parentTaskObject
            } : (_task_parentTaskObject = task.parentTaskObject) !== null && _task_parentTaskObject !== void 0 ? _task_parentTaskObject : existing.parentTaskObject,
            ...task.fields ? {
                fields: mergeTaskFields(existing.fields, task.fields)
            } : {
                fields: existing.fields
            }
        };
    });
